import React, { useState, useMemo } from "react";
import { RotateCcw, FlaskConical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { computeService } from "@/lib/calc";
import { SectionCard, SectionTitle, InfoBox } from "@/components/ui-kit";
import { GuidedTip } from "@/components/GuidedTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-2 focus-visible:ring-2 focus-visible:ring-brand-champagne";

// Read-only temporary scenario. Never mutates the saved service.
export function WhatIf({ service }) {
  const { t, user, fmt } = useAuth();
  const [open, setOpen] = useState(false);
  const baseValues = {
    current_price: service.current_price ?? "",
    duration_min: service.duration_min ?? "",
    desired_margin: service.desired_margin ?? 30,
  };
  const [ov, setOv] = useState(baseValues);

  const baseline = useMemo(() => computeService(service, user), [service, user]);
  const sim = useMemo(() => computeService({
    ...service,
    current_price: ov.current_price === "" ? null : Number(ov.current_price),
    duration_min: Number(ov.duration_min) || 0,
    desired_margin: Number(ov.desired_margin) || 0,
  }, user), [service, ov, user]);

  const reset = () => setOv(baseValues);

  const explanations = [];
  if (Number(ov.duration_min) !== Number(service.duration_min || 0)) explanations.push("whatif.explainTime");
  if ((ov.current_price === "" ? null : Number(ov.current_price)) !== (service.current_price ?? null)) explanations.push("whatif.explainPrice");
  if (Number(ov.desired_margin) !== Number(service.desired_margin || 0)) explanations.push("whatif.explainMargin");

  const Row = ({ label, a, b }) => (
    <div className="grid grid-cols-3 items-center gap-2 py-1.5 text-sm">
      <span className="text-brand-taupe">{label}</span>
      <span className="text-right tabular-nums text-brand-anthracite">{a}</span>
      <span className="text-right tabular-nums font-semibold text-brand-champagne">{b}</span>
    </div>
  );

  return (
    <SectionCard data-testid="whatif-card">
      <div className="flex items-center justify-between">
        <SectionTitle sub={open ? t("whatif.subtitle") : undefined}>{t("whatif.title")}</SectionTitle>
        {!open && (
          <Button variant="outline" onClick={() => setOpen(true)} data-testid="whatif-open" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand h-9">
            <FlaskConical className="mr-2 h-4 w-4" /> {t("whatif.open")}
          </Button>
        )}
      </div>

      {open && (
        <div className="space-y-4">
          <GuidedTip id="whatif">{t("tips.whatif")}</GuidedTip>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("whatif.price")}</Label>
              <Input type="number" min="0" value={ov.current_price} onChange={(e) => setOv({ ...ov, current_price: e.target.value })} className={inputClass} data-testid="whatif-price" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("whatif.duration")}</Label>
              <Input type="number" min="0" value={ov.duration_min} onChange={(e) => setOv({ ...ov, duration_min: e.target.value })} className={inputClass} data-testid="whatif-duration" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("whatif.margin")} %</Label>
              <Input type="number" min="0" max="99" value={ov.desired_margin} onChange={(e) => setOv({ ...ov, desired_margin: e.target.value })} className={inputClass} data-testid="whatif-margin" />
            </div>
          </div>

          <div className="rounded-xl border border-brand-line p-3" data-testid="whatif-results">
            <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-brand-taupe border-b border-brand-line pb-1 mb-1">
              <span></span><span className="text-right">{t("whatif.baseline")}</span><span className="text-right">{t("whatif.simulation")}</span>
            </div>
            <Row label={t("whatif.effTime")} a={`${baseline.effectiveMin.toFixed(0)} ${t("common.min")}`} b={`${sim.effectiveMin.toFixed(0)} ${t("common.min")}`} />
            <Row label={t("whatif.timeCost")} a={fmt(baseline.timeCost)} b={fmt(sim.timeCost)} />
            <Row label={t("whatif.baseCost")} a={fmt(baseline.baseCost)} b={fmt(sim.baseCost)} />
            <Row label={t("whatif.recPrice")} a={fmt(baseline.commercial.nice)} b={fmt(sim.commercial.nice)} />
            <Row label={t("whatif.curMargin")} a={baseline.current ? `${baseline.current.margin.toFixed(0)}%` : "—"} b={sim.current ? `${sim.current.margin.toFixed(0)}%` : "—"} />
          </div>

          {explanations.length > 0 && (
            <InfoBox tone="blush">
              <ul className="space-y-1 text-sm">
                {explanations.map((k) => <li key={k} data-testid={`whatif-explain-${k}`}>{t(k)}</li>)}
              </ul>
            </InfoBox>
          )}

          <Button variant="outline" onClick={reset} data-testid="whatif-reset" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand h-9">
            <RotateCcw className="mr-2 h-4 w-4" /> {t("whatif.reset")}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
